
import { Injectable, Injector } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { BroadcastService } from '../../services/broadcast.service';
import { DeviceGroup } from '../models/device-group.model';
import { SceneBundle } from '../models/scene-bundle.model';
import { SceneSchedules } from '../models/scene-schedules.model';
import { SceneStatus } from '../models/scene-status.model';
import { SceneApiService } from '../services/scene-api.service';
import { ToroLocationState } from './../../accounts/store/toro-location/toro-location.entity';
import { LightingState } from './../../lighting/store/lighting.entity';
import { getAllScenes, getAllSceneScheduleSummary } from './../../lighting/store/lighting.selector';
import { SceneScheduleSummaryState } from './../../lighting/store/scene-schedule-summary/scene-schedule-summary.entity';
import { scenesActionTypes } from './../../lighting/store/scenes/scene.actions';
import { SceneState } from './../../lighting/store/scenes/scene.entity';
import { ScheduleSummary } from './../models/schedule-summary.model';
@Injectable()
export class SceneManagerService {
    private _sceneStatusDict: { [locationId: string]: SceneStatus[] } = {};
    private _sceneBundlesDict: { [locationId: string]: SceneBundle[] } = {};
    private sceneScheduleSummaryList: ScheduleSummary[];
    constructor(private broadcastService: BroadcastService,
        private store: Store<ToroLocationState>,
        private sceneStore: Store<SceneState>,
        private sceneScheduleStore: Store<SceneScheduleSummaryState>,
        private lightingStore: Store<LightingState>,
        private injector: Injector,
        private sceneApiService: SceneApiService) {

        this.broadcastService.userSignedOut.subscribe(() => {
            this.clearCache();
        });
    }

    private clearCache() {
        this._sceneStatusDict = {};
    }

    public getSceneBundle(locationId: number, sceneId: number): SceneBundle {
        let sceneBundlesForLocation;
        if (this._sceneBundlesDict[locationId]) {
            sceneBundlesForLocation = this._sceneBundlesDict[locationId];
        }
        else {
            this.lightingStore.select(getAllScenes).subscribe(allScenes => {
                let sceneBundles: SceneBundle[] = [];
                let scenesStatuses = [];
                if (allScenes.length > 0) {
                    let locId = allScenes[0].LocationId;
                    this.getSceneStatuses(locId).subscribe(statuses => {
                        scenesStatuses = statuses;
                    });

                    this.lightingStore.select(getAllSceneScheduleSummary).subscribe(summaryList => {
                        this.sceneScheduleSummaryList = summaryList;

                        allScenes.forEach(deviceGroup => {
                            const sceneStatus = scenesStatuses.find(status => status.SceneId === deviceGroup.Id);
                            const sceneScheduleSummary = this.sceneScheduleSummaryList.find(summary => summary.Id === deviceGroup.Id);
                            let sceneSchedules = (sceneScheduleSummary && sceneScheduleSummary.Schedule) ? sceneScheduleSummary.Schedule : null;
                            sceneBundles.push(new SceneBundle(deviceGroup, sceneStatus, sceneSchedules, this.injector));
                        });

                        this.setSceneBundles(locId, sceneBundles);

                        sceneBundlesForLocation = this._sceneBundlesDict[locId];
                    });

                }
                else {
                    this.sceneStore.dispatch(scenesActionTypes.getScenesAction({ locationId: locationId }));
                }

            });
        }

        if (sceneBundlesForLocation) {
            return sceneBundlesForLocation.find(scene => scene.DeviceGroup.Id === sceneId) || null;
        }

        return null;
    }

    public getAllSceneBundles(locationId: number): SceneBundle[] {
        let sceneBundlesForLocation = this._sceneBundlesDict[locationId];
        if (sceneBundlesForLocation) {
            return sceneBundlesForLocation.length > 0 ? sceneBundlesForLocation : null;
        }
        else {

            this.lightingStore.select(getAllScenes).subscribe(scenes => {
                this.lightingStore.select(getAllSceneScheduleSummary).subscribe(sceneScheduleSummaryList => {

                    if ((scenes && scenes !== null && scenes.length > 0) && (sceneScheduleSummaryList && sceneScheduleSummaryList !== null)) {
                        let sceneBundles: SceneBundle[] = [];
                        let scenesStatuses = [];
                        let locId = scenes[0].LocationId;
                        let SceneScheduleSummaryList = sceneScheduleSummaryList;

                        this.getSceneStatuses(locId).subscribe(statuses => {
                            scenesStatuses = statuses;
                        });

                        scenes.forEach(deviceGroup => {

                            const sceneStatus = scenesStatuses.find(status => status.SceneId === deviceGroup.Id);
                            const sceneScheduleSummary = SceneScheduleSummaryList.find(summary => summary.Id === deviceGroup.Id);
                            let sceneSchedules = (sceneScheduleSummary && sceneScheduleSummary.Schedule) ? sceneScheduleSummary.Schedule : null;
                            sceneBundles.push(new SceneBundle(deviceGroup, sceneStatus, sceneSchedules, this.injector));

                        });

                        this.setSceneBundles(locId, sceneBundles);

                        sceneBundlesForLocation = this._sceneBundlesDict[locId];
                    }
                })

            });

            // this.store.select(getCurrentLocationDetail).subscribe(loc => {
            //     if (loc && loc !== null) {

            //         let sceneBundles: SceneBundle[] = [];
            //         let scenesStatuses = [];
            //         this.getSceneStatuses(loc.Id).subscribe(statuses => {
            //             scenesStatuses = statuses;
            //         });

            //         loc.DeviceGroups.forEach(deviceGroup => {

            //             const sceneStatus = scenesStatuses.find(status => status.SceneId === deviceGroup.Id);
            //             const sceneScheduleSummary = loc.SceneScheduleSummaryList.find(summary => summary.Id === deviceGroup.Id);
            //             sceneBundles.push(new SceneBundle(deviceGroup, sceneStatus, sceneScheduleSummary.Schedule, this.injector));

            //         });

            //         this.setSceneBundles(loc.Id, sceneBundles);

            //         sceneBundlesForLocation = this._sceneBundlesDict[loc.Id];
            //     }
            // });

        }
        if (sceneBundlesForLocation) {
            return sceneBundlesForLocation || null;
        }

        return null;
    }

    public setSceneBundles(locationId: number, sceneBundles: SceneBundle[]) {
        this._sceneBundlesDict[locationId] = sceneBundles;
    }

    public getSceneStatuses(locationId: number, cached: boolean = true): Observable<SceneStatus[]> {
        if (cached && this._sceneStatusDict[locationId]) {
            return of(this._sceneStatusDict[locationId]);
        }

        return this.sceneApiService.getSceneStatuses(locationId)
            .pipe(
                map((response: SceneStatus[]) => {
                    this._sceneStatusDict[locationId] = [];
                    response.forEach(sceneStatus => {
                        this._sceneStatusDict[locationId].push(new SceneStatus(sceneStatus));
                    });

                    return this._sceneStatusDict[locationId];
                })
            );
    }


    public getSceneStatusesList(locationId: number): Observable<SceneStatus[]> {

        return this.sceneApiService.getSceneStatuses(locationId);
        // .pipe(
        //     map((response: SceneStatus[]) => {
        //         this._sceneStatusDict[locationId] = [];
        //         response.forEach(sceneStatus => {
        //             this._sceneStatusDict[locationId].push(new SceneStatus(sceneStatus));
        //         });

        //         return this._sceneStatusDict[locationId];
        //     })
        // );
    }

    public forceSceneOn(sceneId: number, duration: number = 0): Observable<boolean> {
        return this.sceneApiService.forceSceneOn(sceneId, duration);
    }

    public forceSceneOff(sceneId: number): Observable<boolean> {
        return this.sceneApiService.forceSceneOff(sceneId);
    }

    public forceAllScenesOn(locationId: number, duration: number = 0): Observable<boolean> {
        return this.sceneApiService.forceAllScenesOn(locationId, duration);
    }

    public forceAllScenesOff(locationId: number): Observable<boolean> {
        return this.sceneApiService.forceAllScenesOff(locationId);
    }

    public createScene(locationId: number, createSceneModel): Observable<any> {
        return this.sceneApiService.createScene(locationId, createSceneModel);
    }

    public updateScene(sceneId: number, updateScene: boolean, updateSchedules: boolean, updateSceneModel: { Scene: DeviceGroup, ScheduleSet: SceneSchedules }): Observable<boolean> {
        return this.sceneApiService.updateScene(sceneId, updateScene, updateSchedules, updateSceneModel);
    }

    public deleteScene(sceneId: number): Observable<boolean> {
        return this.sceneApiService.deleteScene(sceneId);
    }

    public getAllScenesByLocation(locationId: number): Observable<any> {
        return this.sceneApiService.getAllScenesByLocation(locationId);
    }

    public getThemesByScene(sceneId: number): Observable<any> {
        return this.sceneApiService.getThemesByScene(sceneId);
    }

    // Helper Methods

}
